import type { ReactNode } from 'react'
import { cn } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import { useIsClient } from 'usehooks-ts'
import { useEmbeddedVideo } from 'modules/attachments/api/get-embedded-video'
import {
  isEmbeddedVideoUrlTooLong,
  isValidEmbeddedVideoUrl,
} from 'modules/attachments/utils/attachment-utils'
import type { PostAttachmentInterface } from 'modules/community/types/post-attachment'

interface EmbeddedVideoPreviewProps {
  className?: string
  attachment: PostAttachmentInterface
}

const EmbeddedVideoPreview = ({ attachment, className }: EmbeddedVideoPreviewProps): ReactNode => {
  const isClient = useIsClient()
  const { t } = useTranslation()
  const url = attachment.url ?? ''
  const isValidUrl = isValidEmbeddedVideoUrl(url) && !isEmbeddedVideoUrlTooLong(url)
  const metadata = useEmbeddedVideo({
    url,
    enabled: isClient && isValidUrl,
  })

  if (!isValidUrl || metadata.isError)
    return (
      <p data-testid="embedded-video-preview-unavailable">{t('post.attachments.upload_failed')}</p>
    )

  return (
    <div className={cn('size-full overflow-hidden rounded-lg', className)}>
      <img
        data-testid="embedded-video-thumbnail"
        className="size-full object-cover"
        src={metadata.data?.thumbnail_url ?? attachment.previewUrl ?? undefined}
        alt={metadata.data?.title ?? ''}
      />
    </div>
  )
}

export default EmbeddedVideoPreview
